import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as Logo } from "../../../assets/images/logoXL.svg";
import { ReactComponent as EyeClose } from "../../../assets/icons/eye-close.svg";
import { ReactComponent as EyeOpen } from "../../../assets/icons/eye-open.svg";
import { ReactComponent as Latte } from "../../../assets/icons/latte.svg";
import { ReactComponent as LinkIcon } from "../../../assets/icons/links.svg";
import { ReactComponent as Spinner } from "../../../assets/icons/spinner.svg";
import { ReactComponent as ArrowRight } from "../../../assets/icons/arrowright.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/icons/arrowleftwhite.svg";
import { ReactComponent as LaptopEmail } from "../../../assets/images/laptopEmail.svg";

import {
  useResetPasswordMutation,
  useResetSetTokenVerifyMutation,
  useLogOutUserMutation,
} from "../../../store/auth/usersApiSlice";
import { logout, clearUserInfo } from "../../../store/auth/authSlice";
import ValidationCheckBox from "../SignUp/ValidationCheckBox";
import { Button, Modal } from "../../common";

const Password = ({ onNext, route = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token: existingSessionToken } = useSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasLowercase, setHasLowercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialCharacter, setHasSpecialCharacter] = useState(false);
  const [isMinLength, setIsMinLength] = useState(false);
  const [isButtonValid, setIsButtonValid] = useState(false);

  const [newUser, setNewUser] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [prompt, setPrompt] = useState("");

  // 🔐 NEW STATES (ADDED)
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState("");

  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [resetSetTokenVerify] = useResetSetTokenVerifyMutation();
  const [logOutUser] = useLogOutUserMutation();

  // 🔐 UPDATED TOKEN VERIFICATION (LOGIC ONLY)
  const handleVerifyToken = async (email, token) => {
    try {
      setIsVerifyingToken(true);
      setIsTokenInvalid(false);

      const res = await resetSetTokenVerify({
        token,
        email,
        create: route === "set" ? true : false,
      }).unwrap();

      if (res?.valid === false) {
        setIsTokenInvalid(true);
        setTokenErrorMessage(
          res?.errors?.non_field_errors?.[0] ||
          "This password reset link is invalid or expired."
        );
      }
    } catch (error) {
      setIsTokenInvalid(true);
      setTokenErrorMessage(
        error?.data?.message ||
        error?.data?.errors?.non_field_errors?.[0] ||
        "This password reset link has expired."
      );
    } finally {
      setIsVerifyingToken(false);
    }
  };

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const newUser = urlParams.get("new_user");
    const userEmail = urlParams.get("email");
    const userToken = urlParams.get("token");
    const userPrompt = urlParams.get("prompt");

    if (userEmail && userToken) {
      setToken(userToken);
      setEmail(userEmail);
      setPrompt(userPrompt);
      setNewUser(newUser);
      handleVerifyToken(userEmail, userToken);
    } else {
      setIsTokenInvalid(true);
      setTokenErrorMessage("Invalid password reset link.");
    }
  }, []);

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    setHasUppercase(/[A-Z]/.test(value));
    setHasLowercase(/[a-z]/.test(value));
    setHasNumber(/[0-9]/.test(value));
    setHasSpecialCharacter(/[!@#$%^&*]/.test(value));
    setIsMinLength(value.length >= 8);
  };

  function validate() {
    setIsButtonValid(
      hasSpecialCharacter &&
      hasLowercase &&
      hasNumber &&
      hasUppercase &&
      isMinLength &&
      confirmPassword
    );
  }

  useEffect(() => {
    validate();
  }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setPasswordError("Password did not match");
      return;
    }

    if (email === "" || token === "") {
      setPasswordError("Please try again!");
      return;
    }

    try {
      const res = await resetPassword({
        token,
        email,
        new_password: password,
        create: route === "set" ? true : false,
      }).unwrap();

      if (res) {
        setIsOpen(true);
      }
    } catch (error) {
      setPasswordError(
        error?.data?.email?.[0] ||
        error?.data?.non_field_errors?.[0] ||
        "Please try again"
      );
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleNavigate = async () => {
    setIsOpen(false);

    if (existingSessionToken) {
      await logOutUser({ token: existingSessionToken });
      dispatch(logout());
      dispatch(clearUserInfo());
    }

    if (prompt) {
      navigate("/login/prompt");
    } else {
      if (newUser) {
        navigate("/login/dashboard");
      } else {
        navigate("/login");
      }
    }
  };

  /* ─────────────── NEW UI STATES ─────────────── */

  if (isVerifyingToken) {
    return (
      <div className="h-screen flex flex-col justify-center items-center gap-4">
        <Spinner />
        <p className="text-secondarytwo text-sm">
          Verifying your password reset link...
        </p>
      </div>
    );
  }

  if (isTokenInvalid) {
    return (
      <div className="h-screen flex flex-col justify-center items-center px-4">
        <div className="max-w-[380px] w-full flex flex-col gap-6 items-center text-center">
          {/* <Latte /> */}
          <h2 className="text-dHeading text-[24px] font-semibold">
            Link Expired
          </h2>
          {/* <p className="text-secondarytwo text-sm">
            {tokenErrorMessage}
          </p> */}
          {/* <Button
            onClick={() => navigate("/reset-password")}
            classes="w-full bg-primary text-white py-3 rounded-md font-semibold cursor-pointer"
          >
            Request New Link
          </Button> */}
          <Link
            to="/login"
            className="text-primary text-sm font-semibold underline"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  /* ─────────────── ORIGINAL UI (UNCHANGED) ─────────────── */

  return (
    <>
      {isOpen && (
        <Modal
          noClose={newUser ? false : true}
          modalHandle={() => handleNavigate()}
        >
          <div
            className={`flex flex-col gap-6 justify-center items-center ${newUser ? "p-8" : ""
              }`}
          >
            <div className="flex flex-col gap-6 items-center max-w-[355px]">
              {newUser ? <LaptopEmail /> : <Latte className="max-w-[355px]" />}
              <p
                className={`text-dHeading ${newUser ? "text-[22px]" : "text-[24px]"
                  } font-semibold`}
              >
                {newUser
                  ? "Thank you for registering with TREVEX!"
                  : "Password Changed Successfully"}
              </p>
              <p
                className={`text-secondarytwo ${newUser ? "text-[12px]" : "text-[16px]"
                  }`}
              >
                {newUser
                  ? "Our representative will reach out shortly to confirm your details and next steps."
                  : "your password has been successfully changed"}
              </p>
            </div>
            <Button
              onClick={() => handleNavigate()}
              classes="flex w-full justify-center items-center rounded-md bg-primary px-3 py-3 text-sm font-semibold leading-6 text-white shadow-sm gap-2"
            >
              {!newUser && <ArrowLeft />}
              {newUser ? "Sign In to Your Account" : "Back to login"}
              {newUser && <ArrowRight />}
            </Button>
          </div>
        </Modal>
      )}

      <div className="h-screen flex flex-col items-center justify-between py-2 md:py-5">
        <div className="flex flex-col justify-center items-center sm:w-full px-2 md:w-[425px] gap-8 md:gap-16">
          <div className="flex flex-col items-center justify-center gap-4">
            <Link to="/">
              <Logo />
            </Link>
            <h3 className="font-heading text-f20 md:text-f36 text-primary font-bold text-center">
              Create new password
            </h3>
            <p className="text-secondarytwo text-f12 md:text-f14 text-center tracking-[1.45px]">
              Please enter a password that you have not used on our platform
              before
            </p>
          </div>

          <form className="w-full" onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block mb-0 md:mb-1.5 text-sm text-secondarytwo font-semibold text-left">
                Enter new Password
              </label>
              <div className="relative">
                <input
                  className="w-full py-3 px-4 text-sm text-primary border-b-2 border-primary"
                  type={showPassword ? "text" : "password"}
                  required
                  onChange={handlePasswordChange}
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? <EyeOpen /> : <EyeClose />}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block mb-0 md:mb-1.5 text-sm text-secondarytwo font-semibold text-left">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  className="w-full py-3 px-4 text-sm text-primary border-b-2 border-primary"
                  type={showPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              {passwordError && (
                <p className="text-red-500 text-left mt-2">
                  {passwordError}
                </p>
              )}
            </div>

            <div className="mt-2 mb-6">
              <ValidationCheckBox passed={isMinLength} message="Minimum 8 characters" />
              <ValidationCheckBox passed={hasUppercase} message="One upper case character" />
              <ValidationCheckBox passed={hasLowercase} message="One lower case character" />
              <ValidationCheckBox passed={hasNumber} message="One number character" />
              <ValidationCheckBox passed={hasSpecialCharacter} message="One Special character(e.g. @, $, #)" />
            </div>

            <button
              className={`w-full py-3 text-sm font-semibold text-white rounded-md flex justify-center items-center ${isButtonValid ? "bg-primary" : "bg-disable"
                }`}
              type="submit"
              disabled={!isButtonValid}
            >
              {isLoading ? <Spinner /> : "Next"}
            </button>
          </form>
        </div>

        <div className="text-f12">
          <p>{`Copyright @ ${new Date().getFullYear()} TREVEX Technology Limited`}</p>
          <Link to="/" className="inline-flex items-center gap-2 font-bold">
            Privacy Policy <LinkIcon />
          </Link>
        </div>
      </div>
    </>
  );
};

export default Password;
